<template>
  <Card
    class="flex h-full min-w-0 flex-col overflow-hidden rounded-[1.35rem] border border-[rgba(218,226,238,0.95)] bg-gradient-to-b from-white to-[#fbfcff] shadow-[0_20px_40px_rgba(15,23,42,0.05)]"
  >
    <CardHeader
      class="border-b border-[rgba(227,233,243,0.95)] bg-white px-6 py-5"
    >
      <div
        class="flex items-start justify-between gap-4 max-[640px]:flex-col max-[640px]:items-stretch"
      >
        <div>
          <CardTitle class="m-0 text-[1.2rem] font-extrabold text-[#182033]">
            Client Overview
          </CardTitle>
          <CardDescription class="mt-[0.35rem] text-[#6e7b92]">
            Assigned-client performance summary.
          </CardDescription>
        </div>

        <!-- <Badge
          variant="outline"
          class="w-fit rounded-full border-[rgba(217,226,238,0.95)] bg-[#edf4ff] px-3 py-1 text-[0.78rem] font-extrabold text-[#2855a6]"
        >
          {{ safeRows.length }} Accounts
        </Badge> -->
      </div>
    </CardHeader>

    <CardContent class="flex-1 p-0">
      <div
        v-if="loading"
        class="space-y-3 p-6"
      >
        <Skeleton
          v-for="n in 6"
          :key="n"
          class="h-[12rem] rounded-[1.15rem] bg-stone-100"
        />
      </div>

      <template v-else>
        <!-- Filters -->
        <div
          v-if="safeRows.length"
          class="border-b border-[rgba(227,233,243,0.95)] bg-[#f8fbff] px-6 py-4"
        >
          <div
            class="grid gap-3 md:grid-cols-2 lg:grid-cols-[minmax(240px,1.4fr)_minmax(160px,0.8fr)_minmax(180px,0.9fr)_minmax(180px,0.9fr)]"
          >
            <div class="min-w-0">
              <label
                for="client-overview-search"
                class="mb-1.5 block text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-[#6f7d94]"
              >
                Search Clients
              </label>

              <div class="relative">
                <span
                  class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#8a98ad]"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="m21 21-4.35-4.35m1.35-5.15a6.5 6.5 0 1 1-13 0 6.5 6.5 0 0 1 13 0Z"
                    />
                  </svg>
                </span>

                <input
                  id="client-overview-search"
                  v-model="searchTerm"
                  type="search"
                  placeholder="Search by company name or ID..."
                  class="h-11 w-full rounded-[0.95rem] border border-[rgba(203,216,235,0.95)] bg-white pl-10 pr-3 text-[0.88rem] font-medium text-[#182033] outline-none transition placeholder:text-[#8c99af] focus:border-[#8db8ff] focus:ring-4 focus:ring-[#dceaff]"
                >
              </div>
            </div>

            <div class="min-w-0">
              <label
                for="client-overview-package"
                class="mb-1.5 block text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-[#6f7d94]"
              >
                Package Type
              </label>

              <select
                id="client-overview-package"
                v-model="selectedPackage"
                class="h-11 w-full rounded-[0.95rem] border border-[rgba(203,216,235,0.95)] bg-white px-3 text-[0.88rem] font-bold text-[#182033] outline-none transition focus:border-[#8db8ff] focus:ring-4 focus:ring-[#dceaff]"
              >
                <option value="all">
                  All Plans
                </option>
                <option
                  v-for="packageName in packageOptions"
                  :key="packageName"
                  :value="packageName"
                >
                  {{ packageName }}
                </option>
              </select>
            </div>

            <div class="min-w-0">
              <label
                for="client-overview-performance"
                class="mb-1.5 block text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-[#6f7d94]"
              >
                Performance
              </label>

              <select
                id="client-overview-performance"
                v-model="selectedPerformance"
                class="h-11 w-full rounded-[0.95rem] border border-[rgba(203,216,235,0.95)] bg-white px-3 text-[0.88rem] font-bold text-[#182033] outline-none transition focus:border-[#8db8ff] focus:ring-4 focus:ring-[#dceaff]"
              >
                <option value="all">
                  All Performance
                </option>
                <option value="high">
                  High Completion
                </option>
                <option value="medium">
                  Medium Completion
                </option>
                <option value="low">
                  Low Completion
                </option>
              </select>
            </div>

            <div class="min-w-0">
              <label
                for="client-overview-sort"
                class="mb-1.5 block text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-[#6f7d94]"
              >
                Sort By
              </label>

              <select
                id="client-overview-sort"
                v-model="selectedSort"
                class="h-11 w-full rounded-[0.95rem] border border-[rgba(203,216,235,0.95)] bg-white px-3 text-[0.88rem] font-bold text-[#182033] outline-none transition focus:border-[#8db8ff] focus:ring-4 focus:ring-[#dceaff]"
              >
                <option value="latest">
                  Latest
                </option>
                <option value="name-asc">
                  Client Name A-Z
                </option>
                <option value="name-desc">
                  Client Name Z-A
                </option>
                <option value="completion-desc">
                  Completion High-Low
                </option>
                <option value="completion-asc">
                  Completion Low-High
                </option>
                <option value="tasks-desc">
                  Tasks High-Low
                </option>
                <option value="tickets-desc">
                  Tickets High-Low
                </option>
              </select>
            </div>
          </div>
        </div>

        <!-- Empty: No source rows -->
        <div
          v-if="!safeRows.length"
          class="px-6 py-6"
        >
          <div
            class="grid min-h-[16rem] place-items-center rounded-[1.15rem] border border-dashed border-[rgba(218,226,238,0.95)] bg-[#fbfcff] px-6 text-center"
          >
            <div class="max-w-md">
              <p class="text-[1rem] font-bold text-[#182033]">
                No client overview data yet.
              </p>
              <p class="mt-2 text-[0.92rem] leading-7 text-[#6e7b92]">
                Client analytics will populate once assigned accounts have
                activity.
              </p>
            </div>
          </div>
        </div>

        <!-- Empty: Filtered rows -->
        <div
          v-else-if="!filteredRows.length"
          class="px-6 py-6"
        >
          <div
            class="grid min-h-[16rem] place-items-center rounded-[1.15rem] border border-dashed border-[rgba(218,226,238,0.95)] bg-[#fbfcff] px-6 text-center"
          >
            <div class="max-w-md">
              <p class="text-[1rem] font-bold text-[#182033]">
                No matching clients found.
              </p>
              <p class="mt-2 text-[0.92rem] leading-7 text-[#6e7b92]">
                Try adjusting your search or filters.
              </p>

              <Button
                variant="outline"
                class="mt-4 rounded-[0.9rem] border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] px-4 text-[0.82rem] font-bold text-[#2855a6] hover:bg-[#eef4ff]"
                @click="resetFilters"
              >
                Reset Filters
              </Button>
            </div>
          </div>
        </div>

        <!-- Cards -->
        <div
          v-else
          class="grid grid-cols-1 gap-5 p-6 lg:grid-cols-2 xl:grid-cols-3"
        >
          <article
            v-for="row in paginatedRows"
            :key="row.client_ref || row.client_name"
            class="group rounded-2xl border border-[rgba(218,226,238,0.95)] bg-white p-4 shadow-[0_14px_30px_rgba(15,23,42,0.04)] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-[#c8daf7] hover:shadow-[0_18px_34px_rgba(15,23,42,0.08)]"
          >
            <div class="flex h-full flex-col gap-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <p
                    class="truncate text-[1.02rem] font-extrabold tracking-[-0.01em] text-[#162033]"
                    :title="displayClientName(row)"
                  >
                    {{ displayClientName(row) }}
                  </p>

                  <p class="mt-1 text-[0.84rem] font-medium text-[#6d7c97]">
                    Client ID {{ displayClientRef(row) }}
                  </p>

                  <p class="mt-1 truncate text-[0.88rem] text-[#6d7c97]">
                    Package:
                    <span class="font-semibold text-[#1b2436]">
                      {{ displayPackageName(row) }}
                    </span>
                  </p>
                </div>

                <Badge
                  variant="outline"
                  class="rounded-full border px-3 py-[0.4rem] text-[0.76rem] font-extrabold whitespace-nowrap shadow-sm transition-all duration-200 ease-out"
                  :class="rateClass(normalizedCompletionRate(row))"
                >
                  Completion {{ normalizedCompletionRate(row) }}%
                </Badge>
              </div>

              <div class="space-y-2.5">
                <div class="flex items-center justify-between gap-3">
                  <p class="text-[0.68rem] font-extrabold uppercase tracking-[0.14em] text-[#6f7d94]">
                    Completion Progress
                  </p>
                  <p class="text-[0.8rem] font-bold text-[#162033]">
                    {{ normalizedCompletionRate(row) }}%
                  </p>
                </div>

                <div class="h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div
                    class="h-full rounded-full transition-all duration-300 ease-out"
                    :class="completionBarClass(normalizedCompletionRate(row))"
                    :style="{ width: `${normalizedCompletionRate(row)}%` }"
                  />
                </div>
              </div>

              <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                <div
                  v-for="item in clientMetricCards(row)"
                  :key="item.label"
                  class="rounded-xl border px-3 py-3 shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md"
                  :class="metricCardClass(item.label)"
                >
                  <p
                    class="text-[0.66rem] font-extrabold uppercase tracking-[0.12em] whitespace-nowrap"
                    :class="metricLabelClass(item.label)"
                  >
                    {{ item.label }}
                  </p>
                  <p
                    class="mt-1 text-[1.02rem] font-extrabold tracking-[-0.01em] whitespace-nowrap"
                    :class="metricValueClass(item.label)"
                  >
                    {{ item.value }}
                  </p>
                </div>
              </div>

              <div class="mt-auto flex flex-wrap gap-2 pt-1">
                <Button
                  variant="outline"
                  class="inline-flex min-h-10 items-center gap-2 rounded-[0.9rem] border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] px-[0.8rem] py-[0.55rem] text-[0.8rem] font-bold text-[#2855a6] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-[#96beff] hover:bg-[#edf4ff] hover:shadow-sm active:translate-y-0 active:scale-[0.98]"
                  @click="$emit('view-dashboard', row)"
                >
                  View Dashboard
                </Button>

                <Button
                  v-if="props.canViewReports !== false"
                  variant="outline"
                  class="inline-flex min-h-10 items-center gap-2 rounded-[0.9rem] border-[rgba(49,125,255,0.22)] bg-[#f6f9ff] px-[0.8rem] py-[0.55rem] text-[0.8rem] font-bold text-[#2855a6] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:border-[#96beff] hover:bg-[#edf4ff] hover:shadow-sm active:translate-y-0 active:scale-[0.98]"
                  @click="$emit('view-reports', row)"
                >
                  View Reports
                </Button>

                <Button
                  v-if="props.canViewTasks !== false"
                  class="inline-flex min-h-10 items-center gap-2 rounded-[0.9rem] bg-[#1f7aff] px-[0.8rem] py-[0.55rem] text-[0.8rem] font-bold text-white transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-[#1f7aff] hover:shadow-[0_12px_24px_rgba(31,122,255,0.24)] active:translate-y-0 active:scale-[0.98]"
                  @click="$emit('view-tasks', row)"
                >
                  View Tasks
                </Button>
              </div>
            </div>
          </article>
        </div>

        <!-- Pagination -->
        <div
          v-if="safeRows.length && filteredRows.length"
          class="flex items-center justify-between gap-4 border-t border-[rgba(227,233,243,0.95)] bg-white px-6 py-4 max-[640px]:flex-col max-[640px]:items-stretch"
        >
          <p class="text-[0.84rem] font-bold text-[#6e7b92]">
            Showing {{ showingStart }}-{{ showingEnd }} of
            {{ filteredRows.length }} clients
          </p>

          <div class="flex items-center gap-3 max-[640px]:justify-between">
            <Button
              variant="outline"
              class="min-h-9 rounded-[0.8rem] border-[rgba(49,125,255,0.18)] bg-[#f8fbff] px-3 text-[0.78rem] font-bold text-[#2855a6] disabled:cursor-not-allowed disabled:opacity-45"
              :disabled="currentPage === 1"
              @click="goToPrevPage"
            >
              Prev
            </Button>

            <span class="text-[0.84rem] font-extrabold text-[#182033]">
              Page {{ currentPage }} of {{ totalPages }}
            </span>

            <Button
              variant="outline"
              class="min-h-9 rounded-[0.8rem] border-[rgba(49,125,255,0.18)] bg-[#f8fbff] px-3 text-[0.78rem] font-bold text-[#2855a6] disabled:cursor-not-allowed disabled:opacity-45"
              :disabled="currentPage === totalPages"
              @click="goToNextPage"
            >
              Next
            </Button>
          </div>
        </div>
      </template>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import type { DashboardClientOverviewItem } from "@/types/dashboard";

const props = defineProps<{
  rows: DashboardClientOverviewItem[];
  loading?: boolean;
  canViewReports?: boolean;
  canViewTasks?: boolean;
}>();

defineEmits<{
  (event: "view-dashboard", row: DashboardClientOverviewItem): void;
  (event: "view-reports", row: DashboardClientOverviewItem): void;
  (event: "view-tasks", row: DashboardClientOverviewItem): void;
}>();

const pageSize = 6;

const searchTerm = ref("");
const selectedPackage = ref("all");
const selectedPerformance = ref("all");
const selectedSort = ref("latest");
const currentPage = ref(1);

const safeRows = computed(() => {
  return Array.isArray(props.rows) ? props.rows : [];
});

const packageOptions = computed(() => {
  const packages = new Set<string>();

  safeRows.value.forEach((row) => {
    const packageName = String(row?.package_name || "").trim();

    if (packageName) {
      packages.add(packageName);
    }
  });

  return Array.from(packages).sort((a, b) => a.localeCompare(b));
});

const filteredRows = computed(() => {
  const keyword = searchTerm.value.trim().toLowerCase();

  return safeRows.value.filter((row) => {
    const clientName = String(row?.client_name || "").toLowerCase();
    const clientRef = String(row?.client_ref || "").toLowerCase();
    const packageName = String(row?.package_name || "").toLowerCase();

    const matchesSearch =
      !keyword ||
      clientName.includes(keyword) ||
      clientRef.includes(keyword) ||
      packageName.includes(keyword);

    const matchesPackage =
      selectedPackage.value === "all" ||
      String(row?.package_name || "").trim() === selectedPackage.value;

    const completionRate = normalizedCompletionRate(row);

    const matchesPerformance =
      selectedPerformance.value === "all" ||
      (selectedPerformance.value === "high" && completionRate >= 75) ||
      (selectedPerformance.value === "medium" &&
        completionRate >= 50 &&
        completionRate < 75) ||
      (selectedPerformance.value === "low" && completionRate < 50);

    return matchesSearch && matchesPackage && matchesPerformance;
  });
});

const sortedRows = computed(() => {
  const rows = [...filteredRows.value];

  switch (selectedSort.value) {
    case "name-asc":
      return rows.sort((a, b) =>
        displayClientName(a).localeCompare(displayClientName(b))
      );

    case "name-desc":
      return rows.sort((a, b) =>
        displayClientName(b).localeCompare(displayClientName(a))
      );

    case "completion-desc":
      return rows.sort(
        (a, b) => normalizedCompletionRate(b) - normalizedCompletionRate(a)
      );

    case "completion-asc":
      return rows.sort(
        (a, b) => normalizedCompletionRate(a) - normalizedCompletionRate(b)
      );

    case "tasks-desc":
      return rows.sort(
        (a, b) => safeNumber(b?.total_tasks) - safeNumber(a?.total_tasks)
      );

    case "tickets-desc":
      return rows.sort(
        (a, b) => safeNumber(b?.active_tickets) - safeNumber(a?.active_tickets)
      );

    case "latest":
    default:
      return rows;
  }
});

const totalPages = computed(() => {
  return Math.max(1, Math.ceil(sortedRows.value.length / pageSize));
});

const paginatedRows = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  const end = start + pageSize;

  return sortedRows.value.slice(start, end);
});

const showingStart = computed(() => {
  if (!filteredRows.value.length) return 0;

  return (currentPage.value - 1) * pageSize + 1;
});

const showingEnd = computed(() => {
  if (!filteredRows.value.length) return 0;

  return Math.min(currentPage.value * pageSize, filteredRows.value.length);
});

watch([searchTerm, selectedPackage, selectedPerformance, selectedSort], () => {
  currentPage.value = 1;
});

watch(totalPages, (pageCount) => {
  if (currentPage.value > pageCount) {
    currentPage.value = pageCount;
  }
});

function goToPrevPage() {
  if (currentPage.value > 1) {
    currentPage.value -= 1;
  }
}

function goToNextPage() {
  if (currentPage.value < totalPages.value) {
    currentPage.value += 1;
  }
}

function resetFilters() {
  searchTerm.value = "";
  selectedPackage.value = "all";
  selectedPerformance.value = "all";
  selectedSort.value = "latest";
  currentPage.value = 1;
}

function displayClientName(row: DashboardClientOverviewItem) {
  return String(row?.client_name || "").trim() || "Unnamed Client";
}

function displayClientRef(row: DashboardClientOverviewItem) {
  return String(row?.client_ref || "").trim() || "Unknown ID";
}

function displayPackageName(row: DashboardClientOverviewItem) {
  return String(row?.package_name || "").trim() || "-";
}

function safeNumber(value: unknown) {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : 0;
}

function normalizedCompletionRate(row: DashboardClientOverviewItem) {
  const rate = safeNumber(row?.completion_rate);

  return Math.max(0, Math.min(100, Number(rate.toFixed(2))));
}

function clientMetricCards(row: DashboardClientOverviewItem) {
  return [
    { label: "Services", value: safeNumber(row?.active_services_count) },
    { label: "Tasks", value: safeNumber(row?.total_tasks) },
    { label: "In Progress", value: safeNumber(row?.in_progress) },
    { label: "Completed", value: safeNumber(row?.completed) },
    { label: "Overdue", value: safeNumber(row?.overdue) },
    { label: "Tickets", value: safeNumber(row?.active_tickets) },
    { label: "Leads", value: safeNumber(row?.new_leads) },
  ];
}

function rateClass(rate: number) {
  if (rate >= 76) return "bg-emerald-50 text-emerald-700 border-emerald-100";
  if (rate >= 51) return "bg-blue-50 text-blue-700 border-blue-100";
  if (rate >= 26) return "bg-amber-50 text-amber-700 border-amber-100";

  return "bg-orange-50 text-orange-700 border-orange-100";
}

function completionBarClass(rate: number) {
  if (rate >= 76) return "bg-emerald-500";
  if (rate >= 51) return "bg-blue-500";
  if (rate >= 26) return "bg-amber-500";

  return "bg-orange-500";
}

function metricCardClass(label: string) {
  switch (label) {
    case "Services":
      return "border-slate-100 bg-slate-50/80 hover:border-slate-200";
    case "Tasks":
      return "border-blue-100 bg-blue-50/80 hover:border-blue-200";
    case "In Progress":
      return "border-purple-100 bg-purple-50/80 hover:border-purple-200";
    case "Completed":
      return "border-emerald-100 bg-emerald-50/80 hover:border-emerald-200";
    case "Overdue":
      return "border-orange-100 bg-orange-50/80 hover:border-orange-200";
    case "Tickets":
      return "border-cyan-100 bg-cyan-50/80 hover:border-cyan-200";
    case "Leads":
      return "border-pink-100 bg-pink-50/80 hover:border-pink-200";
    default:
      return "border-slate-100 bg-slate-50/80 hover:border-slate-200";
  }
}

function metricLabelClass(label: string) {
  switch (label) {
    case "Services":
      return "text-slate-600";
    case "Tasks":
      return "text-blue-700";
    case "In Progress":
      return "text-purple-700";
    case "Completed":
      return "text-emerald-700";
    case "Overdue":
      return "text-orange-700";
    case "Tickets":
      return "text-cyan-700";
    case "Leads":
      return "text-pink-700";
    default:
      return "text-slate-600";
  }
}

function metricValueClass(label: string) {
  switch (label) {
    case "Services":
      return "text-slate-900";
    case "Tasks":
      return "text-blue-950";
    case "In Progress":
      return "text-purple-950";
    case "Completed":
      return "text-emerald-950";
    case "Overdue":
      return "text-orange-950";
    case "Tickets":
      return "text-cyan-950";
    case "Leads":
      return "text-pink-950";
    default:
      return "text-slate-900";
  }
}
</script>
